import { useState } from 'react';
import type { GridConfig, GridRow, GridCellType, GridRowOrder } from '@hcc/types';
import { Button } from '../../ui/Button';
import { OptionsEditor } from './OptionsEditor';
import type { ConditionSourceField } from './ConditionEditor';
import { GridColumnsEditor } from './GridColumnsEditor';
import { GridRowOrderEditor } from './GridRowOrderEditor';
import { Plus, X, ChevronDown, ChevronRight } from 'lucide-react';

interface GridEditorProps {
  grid: GridConfig;
  onChange: (next: GridConfig) => void;
  /** Yes/No fields on the form, offered as the optional "only order if" gate. */
  gateFields?: ConditionSourceField[];
}

const cellClass = 'rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1.5 text-sm';

/** Configures a generic grid: labelled rows (with optional per-row input) and typed columns. */
export function GridEditor({ grid, onChange, gateFields = [] }: GridEditorProps) {
  const [openRow, setOpenRow] = useState<number | null>(null);

  function setRows(rows: GridRow[]) {
    onChange({ ...grid, rows });
  }
  function setRow(i: number, patch: Partial<GridRow>) {
    setRows(grid.rows.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  }

  // Toggle / patch the per-row procurement metadata. Turning it off drops the
  // block entirely so the row stops being demand on the Kit to order screen.
  function patchOrder(i: number, patch: Partial<GridRowOrder>) {
    const current = grid.rows[i]!.order;
    if (!current) return;
    setRow(i, { order: { ...current, ...patch } });
  }
  function toggleOrder(i: number, on: boolean) {
    if (!on) return setRow(i, { order: undefined });
    setRow(i, {
      order: { item: grid.rows[i]!.label || 'Item', category: 'Uniform' },
    });
  }

  // Switching a row's input type, clearing the config that no longer applies.
  function setRowCell(i: number, val: string) {
    if (val === '') return setRow(i, { cell: undefined, options: undefined, mask: undefined });
    if (val === 'select')
      return setRow(i, { cell: 'select', options: grid.rows[i]!.options ?? [], mask: undefined });
    if (val === 'checkbox')
      return setRow(i, { cell: 'checkbox', options: undefined, mask: undefined });
    setRow(i, { cell: val as GridCellType, options: undefined });
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-1.5 text-xs font-medium text-fg-muted">Rows</div>
        <p className="mb-1.5 text-xs text-fg-dim">
          Each row uses the column's input by default. Set a row to a dropdown, or to text / number
          with a mask, to control just that row (e.g. most rows a size dropdown, one a
          waist/length).
        </p>
        <div className="space-y-2">
          {grid.rows.map((r, i) => (
            <div key={i} className="rounded-token-sm border border-line/10 bg-surface p-2">
              <div className="flex items-center gap-2">
                <input
                  value={r.label}
                  placeholder="Row label"
                  onChange={(e) => setRow(i, { label: e.target.value })}
                  className={'flex-1 ' + cellClass}
                />
                <select
                  value={r.cell ?? ''}
                  onChange={(e) => setRowCell(i, e.target.value)}
                  className={cellClass}
                >
                  <option value="">Column default</option>
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="select">Dropdown</option>
                  <option value="checkbox">Checkbox</option>
                </select>
                {r.cell === 'select' ? (
                  <button
                    type="button"
                    onClick={() => setOpenRow(openRow === i ? null : i)}
                    className="rounded-token-sm p-1.5 text-fg-dim hover:bg-surface-2"
                    aria-label="Edit options"
                  >
                    {openRow === i ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setRows(grid.rows.filter((_, j) => j !== i))}
                  className="rounded-token-sm p-1.5 text-fg-dim hover:bg-surface-2 hover:text-danger"
                  aria-label="Remove row"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {r.cell === 'select' && openRow === i ? (
                <div className="mt-2 border-t border-line/10 pt-2">
                  <OptionsEditor
                    options={r.options ?? []}
                    onChange={(opts) => setRow(i, { options: opts })}
                  />
                </div>
              ) : null}
              {r.cell === 'text' || r.cell === 'number' ? (
                <label className="mt-2 block text-xs text-fg-muted">
                  Input mask (optional)
                  <input
                    value={r.mask ?? ''}
                    onChange={(e) => setRow(i, { mask: e.target.value || undefined })}
                    className={'mt-1 block w-full font-mono ' + cellClass}
                    placeholder="e.g. ##/## for waist/length"
                  />
                </label>
              ) : null}

              <GridRowOrderEditor
                row={r}
                gateFields={gateFields}
                onToggle={(on) => toggleOrder(i, on)}
                onPatch={(patch) => patchOrder(i, patch)}
              />
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<Plus className="h-4 w-4" />}
            onClick={() =>
              setRows([
                ...grid.rows,
                { key: `row_${grid.rows.length + 1}`, label: `Row ${grid.rows.length + 1}` },
              ])
            }
          >
            Add row
          </Button>
        </div>
      </div>

      <GridColumnsEditor columns={grid.columns} onChange={(columns) => onChange({ ...grid, columns })} />
    </div>
  );
}
